import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, throttleTime } from 'rxjs/operators';

export interface Region {
  ID: number;
  Name: string;
}

export interface CapacityManagementOverview {
  capacityManagement: CapacityManagement;
}

export interface CapacityManagement {
  regionId: number;
  regionName?: string;
  estimatedOrdersPerShift?: number;
  dates: CapacityManagementDate[];
  subcontractors?: Subcontractor[];
  experienceId?: string;
}

export interface CapacityManagementDate {
  date: string;
  orderCount: number;
  ordersPerShift: number;
  subcontractorShiftCounts: ShiftCount[];
}

export interface ShiftCount {
  subcontractorID: string;
  shiftCount: number;
}

export interface Subcontractor {
  id: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class CarrierCommitmentsService {
  private regions$$ = new BehaviorSubject<Region[]>([]);
  private regionsThrottle$$ = new Subject();
  private regionsShared$ = this.regions$$.asObservable();

  public get regions$(): Observable<Region[]> {
    this.regionsThrottle$$.next();
    return this.regionsShared$;
  }

  constructor(private httpClient: HttpClient) {
    this.regionsThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadRegions();
    });
  }

  private async loadRegions() {
    const response = await this.httpClient
      .get<any>(`${environment.api}/v1/internal/regions`)
      .pipe(map((value) => value.loHiRegions as Region[]))
      .toPromise();
    this.regions$$.next(response.sort((a, b) => a.Name.localeCompare(b.Name)));
  }

  public getCarrierCapacity(regionID: number, start: Date, end: Date) {
    return this.httpClient.get<CapacityManagementOverview>(
      `${environment.api}/v1/internal/capacity_management/${regionID}?startDate=${
        start.toISOString().split('T')[0]
      }&endDate=${end.toISOString().split('T')[0]}`,
    );
  }

  public updateCapacity(toSend: CapacityManagement) {
    return this.httpClient.post(`${environment.api}/v1/internal/capacity_management`, toSend).toPromise();
  }
}
